/** Every date from eventsList.cfc is ISO 8601 or "" (see eventsList.cfc's isoOrEmpty(), same
 *  convention as event-page's eventPage.cfc) - parsed defensively so an unparseable/empty value
 *  renders as the fallback rather than "Invalid Date" or a thrown error. Same base helpers as
 *  web/pages/event-page/src/data/format.ts. */
export function formatDate(value: string | undefined | null, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, opts)
}

/** Every time in this app (signup open/close, event schedule) is entered and displayed as Eastern
 *  - there's no per-event/per-track timezone data to convert from, so this labels the clock time
 *  with a literal " ET" rather than attempting a real timeZone conversion (which would just
 *  relabel the same wall-clock value, or worse, shift it if a viewer's browser timezone differs). */
export function formatDateTime(value: string | undefined | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ET`
}

export function formatMoney(value: number | string | undefined | null, currency = 'USD'): string {
  const n = Number(value)
  if (!value || Number.isNaN(n) || n === 0) return ''
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n)
  } catch {
    return `$${n.toFixed(2)}`
  }
}

/** "Sat Oct 3" (single day) or "Wed Nov 18 – Sun Nov 22" (multi-day), matching Main.dc.html's
 *  card-header date line. Falls back to whichever end actually parses. */
export function formatEventDateRange(startDT: string, endDT: string): string {
  const startDay = formatDate(startDT, { weekday: 'short', month: 'short', day: 'numeric' })
  const endDay = formatDate(endDT, { weekday: 'short', month: 'short', day: 'numeric' })
  if (!startDay) return endDay
  if (!endDay || sameCalendarDay(startDT, endDT)) return startDay
  return `${startDay} – ${endDay}`
}

function sameCalendarDay(a: string, b: string): boolean {
  const da = new Date(a)
  const db = new Date(b)
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false
  return da.toDateString() === db.toDateString()
}

/** "October 2026" month-group header, from any date inside that month. */
export function formatMonthName(value: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** Desktop list row's compact date column: {dow, day, span} - "SAT" / "10" / "Oct 10–11". */
export function formatDateColumn(startDT: string, endDT: string): { dow: string; day: string; span: string } {
  const d = new Date(startDT)
  if (Number.isNaN(d.getTime())) return { dow: '', day: '', span: '' }
  const dow = d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()
  const day = String(d.getDate())
  const endDay = new Date(endDT)
  const multiDay = !Number.isNaN(endDay.getTime()) && !sameCalendarDay(startDT, endDT)
  const month = d.toLocaleDateString(undefined, { month: 'short' })
  const span = multiDay ? `${month} ${d.getDate()}–${endDay.getDate()}` : month
  return { dow, day, span }
}

/** Typed-initials fallback tile (NoFlyerTile) - two letters from the first two significant words
 *  of the event name, skipping a leading year/number token (e.g. "2026 Team Associated..." ->
 *  "TA", not "20"). */
export function eventInitials(name: string): string {
  const words = name
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .filter((w) => !/^\d+$/.test(w))
  if (words.length === 0) return '??'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

// A small, fixed set of dark tile backgrounds (same family as the design's sample data: #15171a,
// #3a2a1c, #1c2a3a, #2a3a24, #3a1c1c, #2a2d31) - picked deterministically from the event id so the
// same event always gets the same tile color across renders/views, with no real "event color"
// data to draw from.
const TILE_COLORS = ['#15171a', '#3a2a1c', '#1c2a3a', '#2a3a24', '#3a1c1c', '#2a2d31']
export function tileColor(id: number): string {
  return TILE_COLORS[id % TILE_COLORS.length]
}
